// §34.6 / §9.1 — Media's registration stub.
//
// **Manifest and thunk only** (D-511), the shape Archive already follows and every Arcade title has
// followed since D-247. Nothing here may `import` the view: a plain import of `Media.tsx` would pull
// the player, the library grid and the YouTube wrapper into the entry chunk, and nothing on screen
// would look different — which is exactly how D-507 discovered the problem it records.
//
// Media holds **one D-059 capability** and so is a factory registered in `App.tsx` (D-520). §34.6
// names no holder for it, which is a gap rather than a decision: the same subsection requires §15's
// global mute to reach the player, and `HangarAPI` is closed over settings by design. Nothing *else*
// it does needs a grant — files arrive through `api.fs`, the library lives under `/Apps/media/`, and
// §34.8's oEmbed route is a plain same-origin `fetch` exactly as §11.4's framing probe already is
// from Browser.
//
// The chunk this thunk produces is named `lazy.Media-<hash>.js` by `vite.config.ts`, which is what
// keeps it out of the service-worker precache by exact prefix (§34.16, D-199's rule, D-511).
import type { InProcessApp, MediaShellAccess } from '@hangar/shared';
import { createLazyApp } from '@/gamekit/lazy';
import { COPY } from '@/copy';
import { MEDIA_WINDOW } from '@/config';

export function createMediaApp(access: MediaShellAccess): InProcessApp {
  return createLazyApp({
    app: {
      id: 'media',
      name: COPY.media.title,
      // §34.1 (D-505) — the wave's launcher home, second app to carry it after Archive.
      category: 'Utilities',
      // §34.1 — "hues chosen at implementation from the §14 ladder and recorded inline". 342 is a
      // crimson nothing else uses (Archive is amber 38); the play-button association is the point,
      // and every other red on the ladder was already spoken for.
      icon: { glyph: 'clapperboard', hue: 342 },
      keywords: ['media', 'player', 'music', 'audio', 'video', 'youtube', 'watch', 'play'],
      // §34.6 — Media is the default opener for both families. `viewer`, not `editor`: it plays a
      // file and never writes one back.
      fileTypes: [
        {
          ext: ['.mp3', '.wav', '.ogg', '.m4a'],
          mime: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4'],
          role: 'viewer',
        },
        { ext: ['.mp4', '.webm'], mime: ['video/mp4', 'video/webm'], role: 'viewer' },
      ],
      // `multiWindow` so two files can play side by side — and so §5.6 restores each window's own
      // `nowPlaying` rather than collapsing them onto one.
      window: { ...MEDIA_WINDOW, multiWindow: true },
    },
    // The `import()` is written here on purpose: a thunk built anywhere else moves the split point.
    load: () => import('./Media'),
    // §34.6 (D-520) — the D-059 capability, injected at composition. It never travels through
    // `mount`, so an external app can never obtain one.
    props: { access },
  });
}
